import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { SingleColumn } from 'components/FeaturedSpreadsheet/components';
import FeaturedSpreadsheetContext from 'components/FeaturedSpreadsheet/context/FeaturedSpreadsheetContext';
import { useLayoutContext } from 'context/LayoutContext';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { equalizeSpreadsheetTitleContentRowHeights, syncSpreadsheetTitleContentRowHeights } from 'utilities';
import getSpreadsheetCellRuntime, { withDatasheetUpdateIdentity } from '../../utilities/getSpreadsheetCellRuntime';
import { ALLOCATION_SCENARIOS_SLUG, VALUATION_GPC_TABLE_SLUG, VALUATION_SUMMARY_SLUG } from '../constants';
import RowResizeHandle from '../RowResizeHandle';
import { TitlesColumnProps } from './TitlesColumn.props';

const getRowLegendNumber = (cell: ICell | undefined): number | undefined => {
  if (!cell?.isLegend) {
    return undefined;
  }

  const value = Number(cell.value);
  return Number.isFinite(value) && value > 0 ? value : undefined;
};

const TitlesColumn = ({
  className = '',
  data,
  dataEditor = undefined,
  defaultRowHeights,
  enableXlsxPreviewLayout = false,
  onRowResize,
  rowHeights,
  tableSlug,
  allowRowLegendSelection,
  disableLegendSelection,
  useDisplayCellRuntime = false,
  virtualization,
}: TitlesColumnProps) => {
  const { cellRenderer, valueViewer } = getSpreadsheetCellRuntime(useDisplayCellRuntime);
  const { onCellsChanged } = useContext(FeaturedSpreadsheetContext);
  const { isExportMode } = useLayoutContext();
  const titlesColumnRootRef = useRef<HTMLDivElement | null>(null);
  const shouldEqualizeExplicitRows = Boolean(rowHeights?.length || defaultRowHeights?.length);
  const usesXlsxTitleColumnEnhancements = Boolean(
    enableXlsxPreviewLayout &&
      (shouldEqualizeExplicitRows || onRowResize || allowRowLegendSelection || disableLegendSelection)
  );

  const rowHeightMap = useMemo(
    () => new Map(rowHeights?.map(({ height, rowNumber }) => [rowNumber, height]) ?? []),
    [rowHeights]
  );
  const defaultRowHeightMap = useMemo(
    () => new Map(defaultRowHeights?.map(({ height, rowNumber }) => [rowNumber, height]) ?? []),
    [defaultRowHeights]
  );

  const decoratedData = useMemo(() => {
    if (!usesXlsxTitleColumnEnhancements) {
      return data;
    }

    return data.map(row => {
      const rowNumber = getRowLegendNumber(row[0]);
      if (!rowNumber) {
        return row;
      }

      const rowHeight = rowHeightMap.get(rowNumber) ?? defaultRowHeightMap.get(rowNumber);
      return row.map((cell, cellIndex) => {
        const isRowLegendCell = cellIndex === 0 && getRowLegendNumber(cell) === rowNumber;
        const shouldResize = isRowLegendCell && onRowResize;

        if (!rowHeight && !shouldResize) {
          return cell;
        }

        return {
          ...cell,
          style: {
            ...(cell.style ?? {}),
            ...(rowHeight ? { height: rowHeight, minHeight: rowHeight, maxHeight: rowHeight } : {}),
            ...(shouldResize ? { overflow: 'visible' as const, position: 'relative' as const } : {}),
          },
          ...(shouldResize
            ? {
              resizeHandle: (
                <>
                  {cell.resizeHandle}
                  <RowResizeHandle
                    defaultHeight={defaultRowHeightMap.get(rowNumber)}
                    onResize={onRowResize}
                    rowNumber={rowNumber}
                  />
                </>
              ),
            }
            : {}),
        };
      });
    });
  }, [data, defaultRowHeightMap, onRowResize, rowHeightMap, usesXlsxTitleColumnEnhancements]);

  const attributesRenderer = useCallback(
    (cell: ICell) => {
      const rowNumber = getRowLegendNumber(cell);
      // Titles and content live in separate <table> elements, so the row-height sync pairs their
      // rows by `data-row-index`. XLSX preview only needs it where there is a row legend; export
      // mode needs it on every row, because sheets without a legend still drift when labels wrap.
      const rowIndex = cell.x ?? (rowNumber ? rowNumber - 1 : undefined);
      const shouldExposeRowIndex = enableXlsxPreviewLayout ? Boolean(rowNumber) : isExportMode;

      return {
        ...(cell.key ? { id: `CELL-${cell.key}` } : {}),
        'data-parent': cell.parent ?? '',
        ...(rowNumber ? { 'data-row-number': rowNumber } : {}),
        ...(shouldExposeRowIndex && typeof rowIndex === 'number'
          ? { 'data-row-index': String(rowIndex) }
          : {}),
      };
    },
    [enableXlsxPreviewLayout, isExportMode]
  );

  const getSpreadsheetContainer = useCallback(() => {
    let currentNode = titlesColumnRootRef.current?.parentElement ?? null;
    const spreadsheetId = `spreadsheet-table__${tableSlug}`;

    while (currentNode) {
      if (currentNode.id === spreadsheetId) {
        return currentNode;
      }
      currentNode = currentNode.parentElement;
    }

    return document.getElementById(spreadsheetId);
  }, [tableSlug]);

  const getSpreadsheetColumnContainers = useCallback(() => {
    const container = getSpreadsheetContainer();
    if (!container) {
      return { contentContainer: null, titlesContainer: null };
    }

    return {
      contentContainer: container.querySelector<HTMLElement>('[id="spreadsheet-table__content-columns"]'),
      titlesContainer: container.querySelector<HTMLElement>('.titles-columns-container'),
    };
  }, [getSpreadsheetContainer]);

  const adjustSummaryValuationTitleCells = useCallback(() => {
    const container = getSpreadsheetContainer();
    if (!container) {
      return;
    }

    const valuationSummaryEmptyCells: NodeListOf<HTMLElement> =
      container.querySelectorAll('table tbody tr td.blank-cell');
    const valuationSummaryTitleCell: HTMLElement | null = container.querySelector(
      'table tbody tr td.spreadsheet-table-title-main > div'
    );

    if (valuationSummaryEmptyCells && valuationSummaryTitleCell) {
      let totalHeight = 0;
      valuationSummaryEmptyCells.forEach(emptyCell => {
        if (emptyCell.offsetHeight) {
          totalHeight += emptyCell.offsetHeight - 1.2;
        }
      });
      valuationSummaryTitleCell.style.height = `${totalHeight}px`;
    }
  }, [getSpreadsheetContainer]);

  const adjustAllocationTitleCells = useCallback(() => {
    const container = getSpreadsheetContainer();
    if (!container) {
      return;
    }

    const baseQuerySelector = 'table tbody tr td.spreadsheet-table-title-main > div';
    const allocationScenarioHeadersCells: NodeListOf<HTMLElement> = container.querySelectorAll(
      `#spreadsheet-table__content-columns ${baseQuerySelector}`
    );
    const allocationScenarioTitle: HTMLElement | null = container.querySelector(
      `#spreadsheet-table__title-columns ${baseQuerySelector}`
    );
    if (allocationScenarioHeadersCells && allocationScenarioTitle) {
      let largerHeight = 0;
      allocationScenarioHeadersCells.forEach(headerCell => {
        if (headerCell.offsetHeight > largerHeight) {
          largerHeight = headerCell.offsetHeight;
        }
      });
      allocationScenarioTitle.style.height = `${largerHeight}px`;
    }
  }, [getSpreadsheetContainer]);

  const adjustExportRowHeights = useCallback(() => {
    const { contentContainer, titlesContainer } = getSpreadsheetColumnContainers();
    if (!titlesContainer || !contentContainer) {
      return;
    }

    syncSpreadsheetTitleContentRowHeights(titlesContainer, contentContainer);
  }, [getSpreadsheetColumnContainers]);

  const equalizeRowHeights = useCallback(
    (forceXlsxRowHeights = false) => {
      const { contentContainer, titlesContainer } = getSpreadsheetColumnContainers();
      if (!titlesContainer || !contentContainer) {
        return;
      }

      equalizeSpreadsheetTitleContentRowHeights(
        titlesContainer,
        contentContainer,
        forceXlsxRowHeights ? { forceBothRows: true, includeXlsxReferenceHeight: true } : undefined
      );
    },
    [getSpreadsheetColumnContainers]
  );

  useLayoutEffect(() => {
    equalizeRowHeights();
  }, [equalizeRowHeights, data]);

  useEffect(() => {
    if (usesXlsxTitleColumnEnhancements) {
      return undefined;
    }

    const timeout = setTimeout(() => {
      if (tableSlug === VALUATION_SUMMARY_SLUG) {
        adjustSummaryValuationTitleCells();
        if (isExportMode) {
          adjustExportRowHeights();
        }
      }
      if (tableSlug === ALLOCATION_SCENARIOS_SLUG) {
        adjustAllocationTitleCells();
      }
      equalizeRowHeights();
    }, 300);

    return () => {
      clearTimeout(timeout);
    };
  }, [
    tableSlug,
    isExportMode,
    usesXlsxTitleColumnEnhancements,
    adjustSummaryValuationTitleCells,
    adjustAllocationTitleCells,
    adjustExportRowHeights,
    equalizeRowHeights,
    data,
  ]);

  // XLSX preview applies explicit row heights and can resize rows, so keep the split title/content tables synced.
  useLayoutEffect(() => {
    if (!usesXlsxTitleColumnEnhancements) {
      return undefined;
    }

    const { contentContainer, titlesContainer } = getSpreadsheetColumnContainers();
    if (!titlesContainer || !contentContainer) {
      return undefined;
    }

    const ownerWindow = titlesColumnRootRef.current?.ownerDocument.defaultView ?? window;
    let animationFrame = 0;
    let followUpAnimationFrame = 0;

    const syncRows = () => {
      if (tableSlug === VALUATION_SUMMARY_SLUG) {
        adjustSummaryValuationTitleCells();
        if (isExportMode) {
          adjustExportRowHeights();
        }
      }
      if (tableSlug === ALLOCATION_SCENARIOS_SLUG) {
        adjustAllocationTitleCells();
      }
      if (tableSlug === VALUATION_GPC_TABLE_SLUG || shouldEqualizeExplicitRows) {
        equalizeRowHeights(shouldEqualizeExplicitRows);
      }
    };

    const scheduleSync = () => {
      if (animationFrame) {
        ownerWindow.cancelAnimationFrame(animationFrame);
      }
      if (followUpAnimationFrame) {
        ownerWindow.cancelAnimationFrame(followUpAnimationFrame);
      }

      animationFrame = ownerWindow.requestAnimationFrame(() => {
        animationFrame = 0;
        syncRows();
        followUpAnimationFrame = ownerWindow.requestAnimationFrame(() => {
          followUpAnimationFrame = 0;
          syncRows();
        });
      });
    };

    scheduleSync();

    const ResizeObserverCtor = ownerWindow.ResizeObserver;
    const resizeObserver = ResizeObserverCtor ? new ResizeObserverCtor(scheduleSync) : null;
    resizeObserver?.observe(titlesContainer);
    resizeObserver?.observe(contentContainer);

    return () => {
      if (animationFrame) {
        ownerWindow.cancelAnimationFrame(animationFrame);
      }
      if (followUpAnimationFrame) {
        ownerWindow.cancelAnimationFrame(followUpAnimationFrame);
      }
      resizeObserver?.disconnect();
    };
  }, [
    tableSlug,
    isExportMode,
    getSpreadsheetColumnContainers,
    usesXlsxTitleColumnEnhancements,
    adjustSummaryValuationTitleCells,
    adjustAllocationTitleCells,
    adjustExportRowHeights,
    equalizeRowHeights,
    shouldEqualizeExplicitRows,
    rowHeights,
    // Re-equalize after GPC row count changes (add/remove comparable companies)
    data,
  ]);

  const datasheetAttributesRenderer = withDatasheetUpdateIdentity(attributesRenderer, useDisplayCellRuntime);

  if (!usesXlsxTitleColumnEnhancements) {
    return (
      <SingleColumn
        isLegend
        data={data}
        className={className}
        dataEditor={dataEditor}
        valueViewer={valueViewer}
        onCellsChanged={onCellsChanged}
        cellRenderer={cellRenderer}
        attributesRenderer={datasheetAttributesRenderer}
        virtualization={virtualization}
      />
    );
  }

  return (
    <div ref={titlesColumnRootRef} style={{ display: 'contents' }}>
      <SingleColumn
        isLegend
        attributesRenderer={datasheetAttributesRenderer}
        data={decoratedData}
        className={className}
        dataEditor={dataEditor}
        valueViewer={valueViewer}
        onCellsChanged={onCellsChanged}
        cellRenderer={cellRenderer}
        allowRowLegendSelection={allowRowLegendSelection}
        disableLegendSelection={disableLegendSelection}
        virtualization={virtualization}
      />
    </div>
  );
};

export default TitlesColumn;
